import {
  Box,
  Button,
  Callout,
  Modal,
  SkeletonText,
} from '@dev-spendesk/grapes';
import { type MonetaryValue } from 'ezmoney';
import { useSelector } from 'react-redux';

import { useQuotes } from 'modules/invoices/transfer/hooks';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useIsCompanyOnSfsUk } from 'src/core/modules/company/hooks';
import { useInvoicePageLocation } from 'src/core/modules/invoices/hooks/useInvoicePageLocation';
import {
  type ExpenseBill,
  type InvoiceBill,
} from 'src/core/modules/invoices/models';
import * as invoicesSelectors from 'src/core/modules/invoices/redux/selectors';
import { QuoteBreakdown } from 'src/core/modules/invoices/transfer/components/QuoteBreakdown';
import { useIsIwtEnabled } from 'src/core/modules/invoices/transfer/hooks/api/useIsIwtEnabled';
import * as expenseClaimsSelectors from 'src/core/modules/reimbursements/redux/selectors';
import { InconsistencyCallout } from 'src/core/modules/verification-of-payee/components/InconsistencyCallout';
import { isInternationalTransfer } from 'src/core/utils';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { WireTransfersUKCallouts } from './WireTransfersUKCallouts';

type WireTransfersSummaryModalProps = {
  selectedPayments: Array<{
    id: string;
    bill: InvoiceBill | ExpenseBill | undefined;
    amount: MonetaryValue;
  }>;
  isApproving: boolean;
  canApprove: boolean;
  showNotEnoughFundsWarning: boolean;
  isOpen: boolean;
  isLoading?: boolean;
  onApprove: () => void;
  onClose: () => void;
};

export const WireTransfersSummaryModal = ({
  selectedPayments,
  isApproving,
  canApprove,
  showNotEnoughFundsWarning,
  isOpen,
  isLoading,
  onApprove,
  onClose,
}: WireTransfersSummaryModalProps) => {
  const { t } = useTranslation('global');

  const page = useInvoicePageLocation();

  const companyId = useCompanyId();

  const isUkCompany = useIsCompanyOnSfsUk();

  const companyCurrency = useCompanyCurrency();

  const count = selectedPayments.length;

  const isIwtEnabled = useIsIwtEnabled();

  const { totalAmountWithQuotes } = useQuotes({
    companyId,
    selectedTransfers: selectedPayments,
    companyCurrency,
  });

  const isVerificationOfPayeeEEAEnabled = useFeature(
    FEATURES.TMP_VERIFICATION_OF_PAYEE_EEA,
  );
  const invalidSelectedPayments = useSelector(
    invoicesSelectors.getSelectedPaymentsWithInvalidVerificationOfPayee,
  );
  const invalidUserScheduledPayments = useSelector(
    expenseClaimsSelectors.getSelectedUsersWithInvalidVerificationOfPayee,
  );
  const showInconsistencyCallout =
    isVerificationOfPayeeEEAEnabled &&
    (invalidSelectedPayments.length > 0 ||
      invalidUserScheduledPayments.length > 0);

  const hasCrossCurrencySelection = selectedPayments.some((it) =>
    isInternationalTransfer({
      sourceCurrency: companyCurrency,
      targetCurrency: it.amount.currency,
      targetCountry: it.bill?.counterparty?.bankAddress?.country,
      iban: it.bill?.counterparty?.bankAddress?.iban,
    }),
  );

  // Check if the selection is only cross-currency to hide some Callouts.
  const hasOnlyCrossCurrencySelection = selectedPayments.every((it) =>
    isInternationalTransfer({
      sourceCurrency: companyCurrency,
      targetCurrency: it.amount.currency,
      targetCountry: it.bill?.counterparty?.bankAddress?.country,
      iban: it.bill?.counterparty?.bankAddress?.iban,
    }),
  );

  if (!selectedPayments.length) {
    return null;
  }

  const onConfirmClicked = () => {
    if (hasCrossCurrencySelection) {
      track(
        AnalyticEventName.INVOICES_CONFIRM_INTERNATIONAL_WIRE_TRANSFER_BUTTON_CLICKED,
      );
    }
    onApprove();
  };

  const firstPaymentAmount = selectedPayments[0].amount;

  const approverDescription = isUkCompany
    ? t(
        'submitMyInvoice.scheduled.wireTransfer.approverModalDescription_sfsuk',
        {
          count,
        },
      )
    : t('submitMyInvoice.scheduled.wireTransfer.approverModalDescription', {
        count,
      });

  const titleLabel = t(
    'submitMyInvoice.scheduled.wireTransfer.controllerModalTitle',
    {
      amount: formatMonetaryValue(firstPaymentAmount),
      count,
    },
  );

  const descriptionLabel = canApprove
    ? approverDescription
    : t('submitMyInvoice.scheduled.wireTransfer.controllerModalDescription', {
        count,
      });

  const confirmActionLabel = canApprove
    ? t('submitMyInvoice.scheduled.wireTransfer.confirmTransfer', { count })
    : t('submitMyInvoice.scheduled.wireTransfer.sendToPayment');

  const cancelActionLabel = t('misc.cancel');

  return (
    <Modal
      actions={[
        <Button
          key="cancel"
          onClick={onClose}
          text={cancelActionLabel}
          variant="secondaryNeutral"
          isDisabled={isApproving}
        />,
        <Button
          key="confirm"
          text={confirmActionLabel}
          variant="primaryBrand"
          onClick={onConfirmClicked}
          isLoading={isApproving || isLoading}
          isDisabled={isApproving}
        />,
      ]}
      iconName="bell"
      iconVariant="info"
      title={isLoading ? '' : titleLabel}
      isOpen={isOpen}
    >
      {!isLoading && (
        <>
          <div className="mt-12 flex flex-col gap-24">
            {isUkCompany && (
              <WireTransfersUKCallouts
                hasCrossCurrencySelection={hasCrossCurrencySelection}
                canApprove={canApprove}
                hasOnlyCrossCurrencySelection={hasOnlyCrossCurrencySelection}
              />
            )}

            <div>{descriptionLabel}</div>

            {showInconsistencyCallout && <InconsistencyCallout />}

            {hasCrossCurrencySelection && isIwtEnabled ? (
              <div className="flex flex-col gap-24">
                <div>
                  {t(
                    'submitMyInvoice.scheduled.wireTransfer.foreignCurrenciesDisclaimer',
                  )}
                </div>

                <QuoteBreakdown isSchedulePage={page === 'schedule'} />

                {page === 'confirm' ? (
                  <Box variant="secondary">
                    <div className="flex justify-between">
                      <p className="text-content-primary body-l">Total</p>
                      <p className="text-content-primary body-l">
                        {formatMonetaryValue(totalAmountWithQuotes)}
                      </p>
                    </div>
                  </Box>
                ) : null}
              </div>
            ) : null}
          </div>

          {showNotEnoughFundsWarning && !canApprove ? (
            <Callout
              title={t(
                'submitMyInvoice.scheduled.wireTransfer.notEnoughFundsControllerWarning',
              )}
              variant="warning"
              className="my-24"
            />
          ) : null}
        </>
      )}

      {isLoading && (
        <>
          <SkeletonText />
          <SkeletonText />
          <SkeletonText />
          <SkeletonText />
        </>
      )}
    </Modal>
  );
};
